/**
 * Form Validation Schema using Zod
 * Hebrew error messages and validation rules
 */

import { z } from 'zod';

const hebrewErrorMessages = {
  required: 'שדה זה הוא חובה',
  invalidEmail: 'כתובת מייל לא תקינה',
  invalidPhone: 'מספר טלפון לא תקין (10 ספרות)',
  invalidDate: 'תאריך לא תקין',
  minAge: 'חייב להיות בן/בת 18 ומעלה',
  minLength: (min: number) => `חייב להיות לפחות ${min} תווים`,
  maxLength: (max: number) => `לא יכול להיות יותר מ-${max} תווים`,
};

export const healthDeclarationSchema = z.object({
  // Personal Info Section
  firstName: z
    .string()
    .min(2, hebrewErrorMessages.minLength(2))
    .max(50, hebrewErrorMessages.maxLength(50)),
  lastName: z
    .string()
    .min(2, hebrewErrorMessages.minLength(2))
    .max(50, hebrewErrorMessages.maxLength(50)),
  dateOfBirth: z
    .string()
    .refine((date) => {
      const birthDate = new Date(date);
      const today = new Date();
      const age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        return age - 1 >= 18;
      }
      return age >= 18;
    }, hebrewErrorMessages.minAge),
  phone: z
    .string()
    .regex(/^\d{10}$/, hebrewErrorMessages.invalidPhone),
  email: z
    .string()
    .email(hebrewErrorMessages.invalidEmail),
  occupation: z
    .string()
    .max(100, hebrewErrorMessages.maxLength(100))
    .optional(),

  // Medical Info Section
  physicalLimitation: z.boolean(),
  physicalLimitationDetails: z
    .string()
    .max(500, hebrewErrorMessages.maxLength(500))
    .optional(),

  bloodPressure: z
    .enum(['high', 'low', 'normal', ''])
    .optional(),
  bloodPressureManaged: z.boolean().optional(),

  surgeries: z.boolean(),
  surgeriesDetails: z
    .string()
    .max(500, hebrewErrorMessages.maxLength(500))
    .optional(),

  heartEvents: z.boolean(),
  heartEventsDetails: z
    .string()
    .max(500, hebrewErrorMessages.maxLength(500))
    .optional(),

  injuries: z.boolean(),
  injuriesDetails: z
    .string()
    .max(500, hebrewErrorMessages.maxLength(500))
    .optional(),

  osteoporosis: z.boolean(),

  arthritis: z.boolean(),
  arthritisManaged: z.boolean().optional(),

  medications: z.boolean(),
  medicationsDetails: z
    .string()
    .max(500, hebrewErrorMessages.maxLength(500))
    .optional(),

  smoking: z.boolean(),
  smokingDetails: z
    .string()
    .max(500, hebrewErrorMessages.maxLength(500))
    .optional(),

  pregnancy: z.boolean().optional(),
  pregnancyWeek: z
    .string()
    .max(50, hebrewErrorMessages.maxLength(50))
    .optional(),

  additionalInfo: z
    .string()
    .max(1000, hebrewErrorMessages.maxLength(1000))
    .optional(),

  // Declaration Section
  agreedToTerms: z
    .boolean()
    .refine((val) => val === true, 'חייב להסכים לתנאים'),
  declarationDate: z.string(),
  signature: z
    .string()
    .refine((val) => val.length > 0, 'חתימה היא חובה'),
});

export type HealthDeclarationFormData = z.infer<typeof healthDeclarationSchema>;

/**
 * Validate individual field
 */
export const validateField = (field: string, value: any) => {
  try {
    const schema = healthDeclarationSchema.pick({ [field]: true });
    schema.parse({ [field]: value });
    return null;
  } catch (error) {
    if (error instanceof z.ZodError) {
      return error.issues[0]?.message || 'שגיאה בתיקוף';
    }
    return 'שגיאה בתיקוף';
  }
};

/**
 * Validate entire form
 */
export const validateForm = (data: any) => {
  try {
    healthDeclarationSchema.parse(data);
    return { valid: true, errors: {} };
  } catch (error) {
    if (error instanceof z.ZodError) {
      const errors: Record<string, string> = {};
      error.issues.forEach((issue: z.ZodIssue) => {
        const path = issue.path.join('.');
        errors[path] = issue.message;
      });
      return { valid: false, errors };
    }
    return { valid: false, errors: { form: 'שגיאה בתיקוף הטופס' } };
  }
};
